import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Select, MenuItem, FormControl,
  InputLabel, TextField, Stack, Card, CardContent, useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminShops } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { useDebounce } from "../../../hooks/useDebounce";
import useListQueryState from "../../../hooks/useListQueryState";

const statusColor = { ACTIVE: "success", SUSPENDED: "warning" };

const ShopList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get("page")) || 0;
  const statusFilter = searchParams.get("status") || "";
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const search = useDebounce(searchInput, 300);
  const limit = 20;

  // See UserList.jsx for why this guards against firing on mount.
  useEffect(() => {
    if (search === (searchParams.get("search") || "")) return;
    updateParams({ search, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-shops", page, statusFilter, search],
    queryFn: () => getAdminShops({ page: page + 1, limit, status: statusFilter || undefined, search: search || undefined }),
  });

  const handleRowClick = (shopId) =>
    navigate(generatePath(frontendRoutes.ADMIN_SHOP_DETAIL, { shopId }));

  if (isLoading) return <CircularProgress />;

  const shops = data?.shops || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Shops</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <TextField
          size="small"
          label="Search shop name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => updateParams({ status: e.target.value, page: undefined })}>
            <MenuItem value="">All</MenuItem>
            {["ACTIVE", "SUSPENDED"].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isMobile ? (
        <Stack spacing={2}>
          {shops.map((s) => (
            <Card key={s._id} variant="outlined" onClick={() => handleRowClick(s._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{s.shopName}</Typography>
                <Typography variant="body2">Owner: {s.createdBy?.name || "—"}</Typography>
                <Chip label={s.status || "ACTIVE"} color={statusColor[s.status || "ACTIVE"]} size="small" sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Shop Name</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shops.map((s) => (
              <TableRow key={s._id} hover onClick={() => handleRowClick(s._id)} sx={{ cursor: "pointer" }}>
                <TableCell>{s.shopName}</TableCell>
                <TableCell>{s.createdBy?.name || "—"}</TableCell>
                <TableCell>{s.country || "—"}</TableCell>
                <TableCell>{s.rating?.toFixed(1) || "—"} ({s.numReviews || 0})</TableCell>
                <TableCell>
                  <Chip label={s.status || "ACTIVE"} color={statusColor[s.status || "ACTIVE"]} size="small" />
                </TableCell>
                <TableCell>{new Date(s.createdAt).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default ShopList;
